import { useState, useEffect } from 'react';
import { saveProductImage, getProductImage, deleteProductImage } from '../../utils/imageStorage';

interface LocalImageUploaderProps {
  productId: string;
  currentImage: string;
  onImageChange?: (newImageUrl: string) => void;
}

export default function LocalImageUploader({ productId, currentImage, onImageChange }: LocalImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [localImage, setLocalImage] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadLocalImage();
  }, [productId]);

  const loadLocalImage = async () => {
    const savedImage = await getProductImage(productId);
    if (savedImage) {
      setLocalImage(savedImage);
      if (onImageChange) {
        onImageChange(savedImage);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setMessage('❌ Veuillez sélectionner une image');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('❌ L\'image est trop grande (max 5MB)');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const base64Image = await saveProductImage(productId, file);
      setLocalImage(base64Image);
      setMessage('✅ Image enregistrée avec succès !');
      
      if (onImageChange) {
        onImageChange(base64Image);
      }

      setUploading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('❌ Erreur lors de l\'enregistrement');
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Voulez-vous vraiment supprimer cette image ?')) return;

    try {
      await deleteProductImage(productId);
      setLocalImage(null);
      setMessage('✅ Image supprimée');
      
      if (onImageChange) {
        onImageChange(currentImage);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('❌ Erreur lors de la suppression');
    }
  };

  const displayImage = localImage || currentImage;

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">
        📸 Modifier l'image du produit
      </h3>

      {/* Aperçu de l'image */}
      <div className="mb-4">
        <img
          src={displayImage}
          alt="Aperçu"
          className="w-full h-64 object-cover rounded-lg border border-gray-200"
        />
        {localImage && (
          <p className="text-sm text-green-600 mt-2">
            ✓ Image personnalisée active
          </p>
        )}
      </div>

      {/* Boutons d'action */}
      <div className="flex gap-3">
        <label className="flex-1 bg-teal-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer text-center whitespace-nowrap">
          {uploading ? '⏳ Upload en cours...' : '📤 Changer l\'image'}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />
        </label>

        {localImage && (
          <button
            onClick={handleDelete}
            className="bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            🗑️ Supprimer
          </button>
        )}
      </div>

      {/* Message de statut */}
      {message && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          message.includes('✅') 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-4">
        💡 L'image est stockée localement dans votre navigateur. Format accepté : JPG, PNG (max 5MB)
      </p>
    </div>
  );
}
